import { t } from "i18next";
import { Add, Trash } from "iconsax-react";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock, { FieldHint } from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSwitch from "../../component/global/DBSwitch";
import AnswerTypeSelect from "./AnswerTypeSelect";
import {
    applyAnswerType,
    createCriterion,
    createState,
    resolveAnswerPresetId,
} from "./helpers";
import "./styles.scss";

function CriterionEditor({ criterion, index, onChange, onDelete, variant }) {
    const isSurvey = variant === "survey";
    const isEmoji = Number(criterion.criteria_state_type) === 3;

    const updateState = (stateIndex, updatedState) => {
        onChange({
            ...criterion,
            states: criterion.states.map((state, itemIndex) =>
                itemIndex === stateIndex ? updatedState : state
            ),
        });
    };

    const addState = () => {
        const nextValue =
            Math.max(0, ...criterion.states.map((state) => state.value)) + 1;
        onChange({
            ...criterion,
            states: [...criterion.states, createState(nextValue)],
        });
    };

    return (
        <div className="ai-form-criterion">
            <div className="ai-form-criterion-header">
                <strong>
                    {t("ai.form.criterion")} {index + 1}
                </strong>
                <button
                    type="button"
                    className="ai-form-delete"
                    title={t("ai.form.remove.criterion")}
                    onClick={onDelete}
                >
                    <Trash size={20} />
                </button>
            </div>
            <div className="ai-form-grid">
                <DBFieldBlock
                    title={
                        isSurvey
                            ? t("survey.form.question.text")
                            : t("criteria.name")
                    }
                    required
                    hint={
                        isSurvey
                            ? t("survey.form.question.text.hint")
                            : undefined
                    }
                >
                    <DBInput
                        size="large"
                        value={criterion.name}
                        placeholder={
                            isSurvey
                                ? t("survey.form.question.text.placeholder")
                                : undefined
                        }
                        onChange={(event) =>
                            onChange({
                                ...criterion,
                                name: event.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("criteria.weight")}
                    required
                    hint={
                        isSurvey
                            ? t("survey.form.question.weight.hint")
                            : undefined
                    }
                >
                    <DBInput
                        type="number"
                        size="large"
                        value={criterion.weight}
                        onChange={(event) =>
                            onChange({
                                ...criterion,
                                weight: Number(event.target.value),
                            })
                        }
                    />
                </DBFieldBlock>
                <DBFieldBlock
                    title={t("criteria.type")}
                    required
                    span={2}
                    hint={
                        isSurvey
                            ? t("survey.form.question.type.hint")
                            : undefined
                    }
                >
                    <AnswerTypeSelect
                        value={resolveAnswerPresetId(criterion)}
                        onChange={(preset) =>
                            onChange(applyAnswerType(criterion, preset))
                        }
                    />
                </DBFieldBlock>
            </div>
            <div className="ai-form-toggles">
                <label>
                    <DBSwitch
                        checked={criterion.add_description}
                        onChange={(value) =>
                            onChange({
                                ...criterion,
                                add_description: value,
                            })
                        }
                    />
                    <span>{t("criteria.explanation")}</span>
                    {isSurvey ? (
                        <FieldHint
                            hint={t("survey.form.question.explanation.hint")}
                        />
                    ) : null}
                </label>
                <label>
                    <DBSwitch
                        checked={criterion.add_upload_file}
                        onChange={(value) =>
                            onChange({
                                ...criterion,
                                add_upload_file: value,
                            })
                        }
                    />
                    <span>{t("criteria.attachment")}</span>
                    {isSurvey ? (
                        <FieldHint
                            hint={t("survey.form.question.attachment.hint")}
                        />
                    ) : null}
                </label>
            </div>
            <div className="ai-form-states">
                <div className="ai-form-states-header">
                    <strong>{t("criteria.modal.states.title")}</strong>
                    {isSurvey ? (
                        <FieldHint
                            hint={t("survey.form.question.options.hint")}
                        />
                    ) : null}
                    <button type="button" onClick={addState}>
                        <Add size={18} />
                        <span>{t("ai.form.add.option")}</span>
                    </button>
                </div>
                {criterion.states.map((state, stateIndex) => (
                    <div
                        className={`ai-form-state${isEmoji ? " is-emoji" : ""}`}
                        key={stateIndex}
                    >
                        {isEmoji ? (
                            <div className="ai-form-emoji-input">
                                <DBInput
                                    size="large"
                                    value={state.emoji}
                                    placeholder={t(
                                        "answer.type.emoji.placeholder"
                                    )}
                                    onChange={(event) =>
                                        updateState(stateIndex, {
                                            ...state,
                                            emoji: event.target.value,
                                        })
                                    }
                                />
                            </div>
                        ) : null}
                        <DBInput
                            size="large"
                            value={state.name}
                            placeholder={t("criteria.state.name")}
                            onChange={(event) =>
                                updateState(stateIndex, {
                                    ...state,
                                    name: event.target.value,
                                })
                            }
                        />
                        <DBInput
                            type="number"
                            size="large"
                            value={state.value}
                            onChange={(event) =>
                                updateState(stateIndex, {
                                    ...state,
                                    value: Number(event.target.value),
                                })
                            }
                        />
                        <button
                            type="button"
                            className="ai-form-delete"
                            title={t("ai.form.remove.option")}
                            onClick={() =>
                                onChange({
                                    ...criterion,
                                    states: criterion.states.filter(
                                        (_, itemIndex) =>
                                            itemIndex !== stateIndex
                                    ),
                                })
                            }
                        >
                            <Trash size={18} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Shared editor for a form structure ({name, criteria[]}) used by both
 * the experience-management AI wizard and the survey module.
 */
export default function CriteriaStructureEditor({
    structure,
    onChange,
    hideName,
    variant,
}) {
    const updateCriterion = (index, updatedCriterion) => {
        onChange({
            ...structure,
            criteria: structure.criteria.map((criterion, itemIndex) =>
                itemIndex === index ? updatedCriterion : criterion
            ),
        });
    };

    return (
        <div className="criteria-structure-editor">
            {!hideName && (
                <DBFieldBlock title={t("form.name")} required>
                    <DBInput
                        size="large"
                        value={structure.name}
                        onChange={(event) =>
                            onChange({
                                ...structure,
                                name: event.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
            )}
            <div className="ai-form-criteria-list">
                {structure.criteria.map((criterion, index) => (
                    <CriterionEditor
                        key={index}
                        index={index}
                        variant={variant}
                        criterion={criterion}
                        onChange={(updatedCriterion) =>
                            updateCriterion(index, updatedCriterion)
                        }
                        onDelete={() =>
                            onChange({
                                ...structure,
                                criteria: structure.criteria.filter(
                                    (_, itemIndex) => itemIndex !== index
                                ),
                            })
                        }
                    />
                ))}
            </div>
            <DBButton
                type="secondary"
                outlined
                caption={
                    <>
                        <Add size={20} />
                        <span>{t("ai.form.add.criterion")}</span>
                    </>
                }
                onClick={() =>
                    onChange({
                        ...structure,
                        criteria: [...structure.criteria, createCriterion()],
                    })
                }
            />
        </div>
    );
}
